import classNames from 'classnames';
import { PropsWithChildren, useId } from 'react';
import styled, { CSSProperties } from 'styled-components';

import { Horizontal } from 'atoms/layout/flex';
import { Img } from 'atoms/ui/Img';
import { Color } from 'style/types';

import dailyDaily from './assets/daily/daily.png';
import dailyStreaks from './assets/daily/streaks.png';
import dailyWeekly from './assets/daily/weekly.png';
import streaksDaily from './assets/streaks/daily.png';
import streaksStreaks from './assets/streaks/streaks.png';
import streaksWeekly from './assets/streaks/weekly.png';
import weeklyDaily from './assets/weekly/daily.png';
import weeklyStreaks from './assets/weekly/streaks.png';
import weeklyWeekly from './assets/weekly/weekly.png';

const icons = {
  weekly: {
    weekly: weeklyWeekly,
    daily: weeklyDaily,
    streaks: weeklyStreaks,
  },
  daily: {
    weekly: dailyWeekly,
    daily: dailyDaily,
    streaks: dailyStreaks,
  },
  streaks: {
    weekly: streaksWeekly,
    daily: streaksDaily,
    streaks: streaksStreaks,
  },
};

const Root = styled.div`
  position: relative;
  --active-color: rgba(var(--main-color), 1);
  --inactive-color: rgba(var(--main-color), 0.16);
  --border-color: var(--active-color);
  z-index: 1;
  height: 100%;
  width: 100%;
  color: rgba(var(--main-color), 1);
  &.inactive {
    z-index: 0;
  }

  &.inactive:not(:hover):not(:disabled) {
    --border-color: var(--inactive-color);
    color: rgba(var(--main-color), 0.5);

    img {
      opacity: 0.5;
    }
  }

  > * {
    transform-origin: center center;
    text-align: center;
  }
`;

const Content = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: relative;
  isolation: isolate;
  height: 100%;
`;

const StyledSvg = styled.svg`
  position: absolute;
  color: var(--active-color);
  z-index: -1;
`;
const StyledImg = styled(Img)`
  width: 40%;
  filter: drop-shadow(0 0 10px rgba(var(--main-color), 0.8));
`;

const AnimatedGlowingBorderSvg = ({
  highlighted,
}: {
  highlighted?: boolean;
}) => {
  const gradientId = useId();
  const glowId = useId();

  return (
    <StyledSvg
      preserveAspectRatio="xMidYMid meet"
      viewBox="0 0 75 75"
      width="100%"
      height="100%"
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0" />
          <stop offset="40%" stopColor="currentColor" stopOpacity="1" />
          <stop offset="60%" stopColor="currentColor" stopOpacity="1" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
        <filter id={glowId}>
          <feGaussianBlur stdDeviation="1" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      {/* Animated glowing border */}
      {highlighted && (
        <rect
          x="11"
          y="11"
          width="53"
          height="53"
          stroke={`url(#${gradientId})`}
          strokeWidth="1"
          filter={`url(#${glowId})`}
          fill="var(--c-black)"
          strokeDasharray="0 216"
          strokeDashoffset="0"
          pathLength="216"
          transform="rotate(45)"
          style={{ transformOrigin: 'center' }}
        >
          {/* Animation for the growing and shrinking effect */}
          <animate
            attributeName="stroke-dasharray"
            values="0 216;30 216;30 216;30 216;0 216"
            keyTimes="0;0.2;0.8;0.9;1"
            dur="3s"
            repeatCount="indefinite"
          />

          {/* Animation for moving around */}
          <animate
            attributeName="stroke-dashoffset"
            values="0;0;-216;-216;-216"
            keyTimes="0;0.2;0.8;0.9;1"
            dur="3s"
            repeatCount="indefinite"
          />
        </rect>
      )}
      <rect
        x="11"
        y="11"
        width="53"
        height="53"
        strokeWidth="1"
        stroke="var(--border-color)"
        transform="rotate(45)"
        style={{ transformOrigin: 'center' }}
      />
    </StyledSvg>
  );
};

type Props = {
  inactive?: boolean;
  highlighted?: boolean;
  type: 'weekly' | 'daily' | 'streaks';
  variant: 'weekly' | 'daily' | 'streaks';
  iconOnly?: boolean;
  color?: Color;
};
export const MissionIcon = ({
  inactive,
  highlighted,
  children,
  iconOnly,
  type,
  variant,
  color,
}: PropsWithChildren<Props>) => {
  if (iconOnly) {
    return <Img src={icons[type][variant]} alt="" width="16" height="16" />;
  }
  return (
    <Root
      className={classNames({ inactive })}
      style={{ '--main-color': color } as CSSProperties}
    >
      <Content>
        <StyledImg src={icons[type][variant]} alt="" width="32" height="32" />
        {children}
        <AnimatedGlowingBorderSvg highlighted={highlighted} />
      </Content>
    </Root>
  );
};
